import { Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { SpinnerService } from '../../../core/services/spinner.service';
import { ManagementsService } from '../services/managements.service';
import { ModalService } from '../../shared/services/modal.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputComponent } from '../../shared/ui/input/input.component';
import { SelectData } from '../../shared/type/input';
import { GeneralErrorComponent } from '../../shared/ui/general-error/general-error.component';
import { ModalData } from '../../shared/type/modal';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-managements-requests',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    ReactiveFormsModule,
    InputComponent,
    GeneralErrorComponent,
  ],
  providers: [],
  templateUrl: './managements-requests.component.html',
  styleUrl: './managements-requests.component.scss',
})
export class ManagementsRequestsComponent implements OnDestroy {
  screenName = 'my-managements-requests';
  requests!: SuperviseRequest[];
  filterRequestForm!: FormGroup;
  subscription: Subscription = new Subscription();

  @Input() filterContentList: SelectData[] = [];
  @Input() filterNameList: SelectData[] = [];
  @Input() filterCompanyList: SelectData[] = [];

  stateCompanyList: SelectData[] = [];
  defaultValueSelect = { value: '', label: 'Todos' };
  filterSelected: FiltersParams = {};

  // Paginación
  nextPage = '';
  hasMoreItems: boolean = false;

  constructor(
    private readonly _managementsService: ManagementsService,
    private readonly _spinnerService: SpinnerService,
    private readonly _modalService: ModalService,
    private readonly _formBuilder: FormBuilder,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.getRequests();
  }

  ngOnInit(): void {
    this.filterRequestForm = this._formBuilder.group({
      content: [''],
      userEmail: [''],
      company: [''],
      state: [''],
    });

    this.stateCompanyList = this._managementsService.statusRequestData.sort(
      (a, b) => a.label.localeCompare(b.label)
    );
    this.filter();
  }

  filter(): void {
    this.filterRequestForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => {
          return JSON.stringify(prev) === JSON.stringify(curr);
        })
      )
      .subscribe((value) => {
        const filters: FiltersParams = {};

        if (value.content) {
          filters.content = value.content;
        }
        if (value.userEmail) {
          filters.userEmail = value.userEmail;
        }
        if (value.company) {
          filters.userCompany = value.company;
        }
        if (value.state) {
          filters.state = parseInt(value.state, 10);
        }
        this.filterSelected = filters;
        const elementId = Object.keys(filters).pop() ?? '';
        const elementValue =
          elementId === 'userEmail' ? '' : (filters as any)[elementId];
        const elementText = elementValue
          ? `${elementId}:${elementValue}`
          : elementId;
        this._dataLayerService.pushEventSearch(
          this.screenName,
          `search-${elementId}`,
          elementText
        );
        this.getRequests(filters, false);
      });
  }

  getRequests(useFilters: FiltersParams = {}, seeMore: boolean = false): void {
    this._spinnerService.show();
    if (this.nextPage && seeMore) {
      useFilters.pageId = this.nextPage;
    }
    const sub = this._managementsService
      .getSupervisesRequests(useFilters)
      .subscribe({
        next: (res) => {
          const response: SupervisesResponse = res;
          const description = seeMore
            ? 'Load more requests'
            : 'Search requests';
          this._dataLayerService.pushEventSubmit(
            this.screenName,
            'manage_requests',
            description,
            'search performed'
          );

          if (seeMore) {
            this.requests = [...(this.requests ?? []), ...response.requests];
          } else {
            this.requests = response.requests ?? [];
          }

          this.nextPage = response.pageId;

          if (this.nextPage) {
            this.hasMoreItems = true;
          } else {
            this.hasMoreItems = false;
          }
        },
        error: (err) => {
          this.requests = [];
          this.nextPage = '';
          this.hasMoreItems = false;
          this._spinnerService.hide();
          this._dataLayerService.pushEventSubmitError(
            this.screenName,
            'manage_requests',
            err.status,
            err.error?.message ?? err.status
          );
        },
        complete: () => {
          this._spinnerService.hide();
        },
      });
    this.subscription.add(sub);
  }

  postRequests(body: SuperviseRequestBody): void {
    this._spinnerService.show();
    const sub = this._managementsService
      .postSupervisesRequests(body)
      .subscribe({
        next: (res) => {
          this._modalService.showSuccessModalService(() => {
            this.getRequests(this.filterSelected, false);
          });
          this._dataLayerService.pushEventSubmit(
            this.screenName,
            'manage_requests',
            `Update request state: ${body.state}`,
            'content actions'
          );
        },
        error: (err) => {
          this._spinnerService.hide();
          this._dataLayerService.pushEventSubmitError(
            this.screenName,
            'manage_requests',
            err.status,
            err.error?.message ?? err.status
          );
          if (err.state == 400) {
            this._modalService.openGenericModal(
              this._managementsService.errorRequestModalData(() => {
                () => {};
              })
            );
          }
        },
        complete: () => {
          this._spinnerService.hide();
        },
      });
    this.subscription.add(sub);
  }

  loadMore(): void {
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'load-more-requests',
      'Load more requests button',
      'content actions'
    );
    this.getRequests(this.filterSelected, true);
  }

  openModal(e: Event, request: SuperviseRequest) {
    let body: SuperviseRequestBody = {
      content: request.content,
      state: 0,
      user: request.email,
    };

    this._managementsService.openPopover(
      e,
      this._managementsService.requestModalData(
        () => {
          body.state = 1;
          this._dataLayerService.pushEventClickButton(
            this.screenName,
            'accept-request',
            'Accept request',
            'content actions'
          );
          this.showSuccessModal(body);
        },
        () => {
          body.state = 2;
          this._dataLayerService.pushEventClickButton(
            this.screenName,
            'decline-request',
            'Decline request',
            'content actions'
          );
          this.showSuccessModal(body);
        },
        request.state,
        request.comments
          ? () => {
              this._modalService.openGenericModal(
                this._managementsService.showCommentsModalData(
                  request.comments || ''
                )
              );
            }
          : undefined
      )
    );
  }

  showSuccessModal(body: SuperviseRequestBody): void {
    const modalData =
      body.state === 1
        ? this._managementsService.acceptRequestModalData(
            () => {},
            () => this.postRequests(body)
          )
        : this._managementsService.declineRequestModalData(
            () => {},
            () => this.postRequests(body)
          );

    this._modalService.openGenericModal(modalData);
  }

  getState(state: number): string {
    const status = this.stateCompanyList.find(
      (item) => item.value === state.toString()
    );
    return status ? status.label : 'Desconocido';
  }

  getCompany(value: number): string {
    const status = this.filterCompanyList.find(
      (item) => item.value === value.toString()
    );
    return status ? status.label : 'Todos';
  }

  getName(value: number): string {
    const status = this.filterNameList.find(
      (item) => item.value === value?.toString()
    );
    return status ? status.label : 'Todos';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
